import { preAuthRequest, provisionUserRequest } from '../../apiRequests'
import { MOBILE_API_URL } from '../../apiEnum'
import Cookies from 'js-cookie'

const getHubspotUTK = () => {
    const cookie = Cookies.get('hubspotutk')
    return cookie || ''
}

/**
 * Check if a user exists in the database by email.
 * Used during login to determine if user needs provisioning.
 * @param {string} email - User's email address
 * @param {string} token - JWT token from Cognito
 * @returns {Object|null} - User object if exists, null otherwise
 */
export const checkUserExists = async (email, token) => {
    const action = {
        action: 'getObjects',
        attributeName: 'userName',
        attributeValue: email,
        class: 'scoop.user.User',
    }
    const result = await preAuthRequest(MOBILE_API_URL, action, token)
    return result?.objects?.length === 1 ? result.objects[0] : null
}

/**
 * Provision a new user account.
 * This Lambda can take up to 60s but API Gateway has 30s timeout.
 * @param {Object} params - User data
 * @param {string} params.email - User's email (becomes userName)
 * @param {string} params.userID - User's unique ID
 * @param {string} params.firstName - User's first name
 * @param {string} params.lastName - User's last name
 * @returns {Response} - Raw fetch response to check status codes
 */
export const provisionNewUser = async ({ email, userID, firstName, lastName }) => {
    const data = {
        userName: email,
        userID,
        firstName,
        lastName,
        hubspotUTK: getHubspotUTK(),
    }
    return await provisionUserRequest(data)
}

/**
 * Record user login for analytics/tracking.
 * Fire-and-forget - errors are logged but don't affect flow.
 * @param {string} userID - User's unique ID
 * @param {string} token - JWT token
 */
export const recordLogin = async (userID, token) => {
    try {
        const data = {
            action: 'login',
            userID,
            skipUserIDOverride: true,
            skipWorkspaceIDOverride: true,
        }
        await preAuthRequest('/ui_information', data, token)
    } catch (error) {
        console.error('Error recording login', error)
    }
}

/**
 * Poll for user creation after a 504 timeout.
 * Lambda may still be running after API Gateway timeout.
 * @param {string} email - User's email to check
 * @param {string} token - JWT token from Cognito
 * @param {Object} config - Polling configuration
 * @param {number} config.maxAttempts - Maximum polling attempts
 * @param {number} config.delayMs - Delay between attempts in ms
 * @returns {Object|null} - User object if found, null if max attempts reached
 */
export const pollForUserCreation = async (email, token, { maxAttempts = 5, delayMs = 3000 }) => {
    for (let attempt = 1; attempt <= maxAttempts; attempt++) {
        await new Promise((resolve) => setTimeout(resolve, delayMs))

        try {
            const user = await checkUserExists(email, token)
            if (user) {
                return user
            }
        } catch (error) {
            console.error(`Polling attempt ${attempt} failed:`, error)
        }
    }
    return null
}
